import { useState, useEffect, useCallback } from 'react';
import { AppleHealthService, HealthDataUpdate } from '../services/appleHealthService';
import { getPlatformInfo, PlatformInfo } from '../utils/platformUtils';

export interface AppleHealthState {
  isAvailable: boolean;
  isInitialized: boolean;
  hasPermissions: boolean;
  isLoading: boolean;
  error: Error | null;
  platformInfo: PlatformInfo | null;
}

export function useAppleHealth() {
  const [state, setState] = useState<AppleHealthState>({
    isAvailable: false,
    isInitialized: false,
    hasPermissions: false,
    isLoading: true,
    error: null,
    platformInfo: null,
  });

  const [service] = useState(() => new AppleHealthService());

  useEffect(() => {
    const initialize = async () => {
      try {
        setState(prev => ({ ...prev, isLoading: true, error: null }));
        const platformInfo = await getPlatformInfo();
        setState(prev => ({ ...prev, platformInfo }));
        if (platformInfo.platform === 'ios') {
          const isAvailable = await service.initialize();
          let hasPermissions = service.getHasPermissions();
          
          // Set initial state first
          setState(prev => ({
            ...prev,
            isAvailable,
            hasPermissions,
            isInitialized: true,
            isLoading: false,
          }));
          
          // Automatically request permissions if not granted
          // Add a delay to ensure the app UI is fully loaded before showing permission dialog
          if (isAvailable && !hasPermissions) {
            console.log('[Apple Health] No permissions found, requesting automatically in 2 seconds...');
            // Wait for app to be fully ready before requesting permissions
            setTimeout(async () => {
              try {
                console.log('[Apple Health] Now requesting permissions...');
                const granted = await service.requestPermissions();
                console.log('[Apple Health] Permission request result:', granted);
                setState(prev => ({
                  ...prev,
                  hasPermissions: granted,
                }));
              } catch (error) {
                console.error('[Apple Health] Error requesting permissions on startup:', error);
              }
            }, 2000); // 2 second delay to ensure UI is fully ready
          }
        } else {
          setState(prev => ({
            ...prev,
            isAvailable: false,
            hasPermissions: false,
            isInitialized: true,
            isLoading: false,
          }));
        }
      } catch (error) {
        setState(prev => ({
          ...prev,
          error: error as Error,
          isLoading: false,
          isInitialized: true,
        }));
      }
    };
    initialize();
  }, [service]);

  const requestPermissions = useCallback(async (): Promise<boolean> => {
    if (!state.isAvailable) throw new Error('Apple Health is not available on this device');
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      const granted = await service.requestPermissions();
      setState(prev => ({
        ...prev,
        hasPermissions: granted,
        isLoading: false,
      }));
      return granted;
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error as Error,
        isLoading: false,
      }));
      throw error;
    }
  }, [service, state.isAvailable]);

  const startImmediateMonitoring = useCallback(async (onUpdate: (update: HealthDataUpdate) => void): Promise<void> => {
    if (!state.isAvailable || !state.hasPermissions) {
      throw new Error('Apple Health not available or permissions not granted');
    }
    await service.startImmediateMonitoring(onUpdate);
  }, [service, state.isAvailable, state.hasPermissions]);

  const stopImmediateMonitoring = useCallback(async () => {
    await service.stopImmediateMonitoring();
  }, [service]);

  return {
    ...state,
    requestPermissions,
    startImmediateMonitoring,
    stopImmediateMonitoring,
    service,
  };
}
